import { createElement, useEffect, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { EmptyState } from '@/components/ui';
import { adoptServerFontSize, USER_FONT_FACTOR, useFontSizePreference } from './fontSize';
import { isModuleEnabled, moduleForPath, moduleLabel, setModuleSnapshot } from './modules';
import { useSession } from './session';
import type { SessionPayload } from './types';

/**
 * Applies the platform's public appearance configuration (`platform.appearance` and
 * `platform.general` from GET /auth/session → platform) to the web app:
 *  - font scale → root font size (all type and spacing tokens are rem-based, so the whole UI scales;
 *    percentages keep the user's browser font preference as the base);
 *  - density → `data-density` plus tighter spacing / control-height tokens for `compact`;
 *  - default locale → `<html lang>`;
 *  - header → product name, help link and banner components below;
 *  - modules → `isModuleEnabled` snapshot and `ModuleGate` for direct navigation.
 * All text is rendered as React text nodes (never as HTML).
 *
 * Scope: the visual settings (brand colour, font scale, density, header / banner) style ONLY the
 * platform (super-admin) console. `PlatformShell` claims the `platform` scope while mounted
 * (`PlatformConsoleAppearanceScope`); everywhere else — tenant staff app, client portal, sign-in
 * pages — the neutral product defaults apply and tenant branding (lib/branding.ts, applied by the
 * workspace shells) stays tenant-specific. Leaving the console (workspace switch, sign-out,
 * navigation) releases the scope and the neutral values are re-applied. Module switches and the
 * default locale are not appearance and keep applying everywhere. The server mirrors this: the
 * session only carries the visual values for platform operators.
 */

export type AppearanceScope = 'platform' | 'default';

let currentScope: AppearanceScope = 'default';
let scopeClaims = 0;
const scopeListeners = new Set<() => void>();

function setScope(next: AppearanceScope) {
  if (next === currentScope) return;
  currentScope = next;
  scopeListeners.forEach((l) => l());
}

/** Claims the platform-console scope; returns the release function (claims are counted). */
export function claimPlatformAppearanceScope(): () => void {
  scopeClaims += 1;
  setScope('platform');
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scopeClaims = Math.max(0, scopeClaims - 1);
    if (scopeClaims === 0) setScope('default');
  };
}

export function getAppearanceScope(): AppearanceScope {
  return currentScope;
}

function subscribeScope(listener: () => void) {
  scopeListeners.add(listener);
  return () => scopeListeners.delete(listener);
}

export function useAppearanceScope(): AppearanceScope {
  return useSyncExternalStore(subscribeScope, getAppearanceScope, getAppearanceScope);
}

/** Renderless: mount inside the platform console shell only. */
export function PlatformConsoleAppearanceScope(): null {
  useEffect(() => claimPlatformAppearanceScope(), []);
  return null;
}

export type FontScale = 'sm' | 'md' | 'lg' | 'xl';
export type Density = 'comfortable' | 'compact';
export type HeaderAccent = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/** Root font size per scale (16px browser default → 15 / 16 / 17 / 18 px). */
export const FONT_SCALE_ROOT: Record<FontScale, string> = { sm: '93.75%', md: '100%', lg: '106.25%', xl: '112.5%' };
export const FONT_SCALE_FACTOR: Record<FontScale, number> = { sm: 0.9375, md: 1, lg: 1.0625, xl: 1.125 };
export const FONT_SCALE_LABEL: Record<FontScale, string> = { sm: 'Small', md: 'Default', lg: 'Large', xl: 'Extra large' };

/** Token overrides for compact density (tokens.css defaults apply for comfortable). */
export const COMPACT_TOKENS: Record<string, string> = {
  '--control-h-sm': '1.5rem',
  '--control-h-md': '2rem',
  '--space-3': '0.625rem',
  '--space-4': '0.75rem',
  '--space-5': '1rem',
  '--space-6': '1.25rem',
};

export const ACCENT_STYLE: Record<HeaderAccent, { background: string; color: string; borderColor: string }> = {
  primary: { background: 'var(--color-primary-soft)', color: 'var(--color-primary-text)', borderColor: 'var(--color-primary)' },
  info: { background: 'var(--color-info-soft)', color: 'var(--color-info-text)', borderColor: 'var(--color-info-border)' },
  success: { background: 'var(--color-success-soft)', color: 'var(--color-success-text)', borderColor: 'var(--color-success-border)' },
  warning: { background: 'var(--color-warning-soft)', color: 'var(--color-warning-text)', borderColor: 'var(--color-warning-border)' },
  danger: { background: 'var(--color-danger-soft)', color: 'var(--color-danger-text)', borderColor: 'var(--color-danger-border)' },
  neutral: { background: 'var(--color-neutral-soft)', color: 'var(--color-neutral-text)', borderColor: 'var(--color-neutral-border)' },
};

export interface PublicAppearance {
  primaryColor?: string;
  fontScale: FontScale;
  density: Density;
  header: { showProductName: boolean; showHelpLink: boolean; helpUrl: string; bannerText: string; accent: HeaderAccent };
}

export interface PublicGeneral {
  productName: string;
  supportEmail: string;
  supportUrl: string;
  termsUrl: string;
  privacyUrl: string;
  defaultLocale: string;
  signupEnabled: boolean;
}

type PlatformConfig = SessionPayload['platform'];

const pick = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T => (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback);
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const httpsOnly = (v: unknown) => {
  const s = str(v);
  try {
    return new URL(s).protocol === 'https:' ? s : '';
  } catch {
    return '';
  }
};

export function readAppearance(platform: PlatformConfig): PublicAppearance {
  const a = (platform?.['platform.appearance'] ?? {}) as Record<string, unknown>;
  const h = (a.header ?? {}) as Record<string, unknown>;
  return {
    primaryColor: typeof a.primaryColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(a.primaryColor) ? a.primaryColor : '#4285F4',
    fontScale: pick(a.fontScale, ['sm', 'md', 'lg', 'xl'] as const, 'md'),
    density: pick(a.density, ['comfortable', 'compact'] as const, 'comfortable'),
    header: {
      showProductName: h.showProductName === true,
      showHelpLink: h.showHelpLink === true,
      helpUrl: httpsOnly(h.helpUrl),
      bannerText: str(h.bannerText).slice(0, 140),
      accent: pick(h.accent, ['primary', 'info', 'success', 'warning', 'danger', 'neutral'] as const, 'primary'),
    },
  };
}

/** Product defaults used outside the platform console (no brand colour override, no header extras). */
export const NEUTRAL_APPEARANCE: PublicAppearance = Object.freeze({
  primaryColor: undefined,
  fontScale: 'md',
  density: 'comfortable',
  header: Object.freeze({ showProductName: false, showHelpLink: false, helpUrl: '', bannerText: '', accent: 'primary' as HeaderAccent }),
}) as PublicAppearance;

/** Appearance that applies in the given scope: platform settings in the console, neutral elsewhere. */
export function appearanceForScope(platform: PlatformConfig, scope: AppearanceScope): PublicAppearance {
  return scope === 'platform' ? readAppearance(platform) : NEUTRAL_APPEARANCE;
}

export function readGeneral(platform: PlatformConfig): PublicGeneral {
  const g = (platform?.['platform.general'] ?? {}) as Record<string, unknown>;
  return {
    productName: str(g.productName) || 'Client Onboarding',
    supportEmail: str(g.supportEmail),
    supportUrl: httpsOnly(g.supportUrl),
    termsUrl: httpsOnly(g.termsUrl),
    privacyUrl: httpsOnly(g.privacyUrl),
    defaultLocale: str(g.defaultLocale),
    signupEnabled: g.signupEnabled !== false,
  };
}

/**
 * Applies scale / density / language to an element (the document root in the app). `userFactor` is
 * the signed-in user's own text size (lib/fontSize.ts) multiplied on top of the platform scale.
 */
export function applyAppearance(root: HTMLElement, a: Pick<PublicAppearance, 'fontScale' | 'density' | 'primaryColor'>, locale?: string, userFactor = 1) {
  const color = a.primaryColor && /^#[0-9a-fA-F]{6}$/.test(a.primaryColor) ? a.primaryColor : undefined;
  if (color) {
    root.style.setProperty('--brand-primary', color);
    const rgb = [1,3,5].map(i => parseInt(color.slice(i,i+2),16)/255).map(v => v <= .04045 ? v/12.92 : ((v+.055)/1.055)**2.4);
    const luminance = .2126*rgb[0]! + .7152*rgb[1]! + .0722*rgb[2]!;
    root.style.setProperty('--brand-on-primary', luminance > .179 ? '#202124' : '#FFFFFF');
  } else {
    // Neutral scope: fall back to the stylesheet defaults (styles/enterprise.css :root).
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-on-primary');
  }
  root.style.fontSize = userFactor === 1 ? FONT_SCALE_ROOT[a.fontScale] : `${+(FONT_SCALE_FACTOR[a.fontScale] * userFactor * 100).toFixed(4)}%`;
  root.dataset.fontScale = a.fontScale;
  root.dataset.density = a.density;
  for (const [token, value] of Object.entries(COMPACT_TOKENS)) {
    if (a.density === 'compact') root.style.setProperty(token, value);
    else root.style.removeProperty(token);
  }
  if (locale && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(locale)) root.lang = locale;
}

/**
 * Mount once for the whole app (inside SessionProvider). Returns the parsed configuration for
 * components that render header elements.
 */
export function usePlatformAppearance() {
  const { platform, user } = useSession();
  const scope = useAppearanceScope();
  const appearance = appearanceForScope(platform, scope);
  const general = readGeneral(platform);
  setModuleSnapshot(platform);
  const serverPref = user?.fontSizePreference;
  useEffect(() => {
    adoptServerFontSize(serverPref);
  }, [serverPref]);
  const [userSize] = useFontSizePreference(!!user);
  const key = `${scope}|${appearance.primaryColor}|${appearance.fontScale}|${appearance.density}|${general.defaultLocale}|${userSize}`;
  useEffect(() => {
    applyAppearance(document.documentElement, appearance, general.defaultLocale, USER_FONT_FACTOR[userSize]);
    document.documentElement.dataset.userFontSize = userSize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return {
    appearance,
    general,
    isModuleEnabled: (moduleKey: string) => isModuleEnabled(moduleKey, platform),
  };
}

/** Renderless bridge: `<PlatformAppearanceBridge />` inside SessionProvider. */
export function PlatformAppearanceBridge(): null {
  usePlatformAppearance();
  return null;
}

/** Platform header banner (plain text), styled with the configured accent token. */
export function PlatformBanner(): ReactNode {
  const { platform } = useSession();
  const { header } = appearanceForScope(platform, useAppearanceScope());
  if (!header.bannerText) return null;
  const style: CSSProperties = { ...ACCENT_STYLE[header.accent], borderBottom: '1px solid', padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-sm)', textAlign: 'center' };
  return createElement('div', { role: 'note', 'aria-label': 'Platform notice', className: 'platform-banner', style }, header.bannerText);
}

/** Product name for the top bar, when enabled. */
export function HeaderProductName({ className = 'topbar-title truncate' }: { className?: string }): ReactNode {
  const { platform } = useSession();
  if (!appearanceForScope(platform, useAppearanceScope()).header.showProductName) return null;
  return createElement('span', { className }, readGeneral(platform).productName);
}

/** External help link for the top bar, when enabled (https only). */
export function HeaderHelpLink(): ReactNode {
  const { platform } = useSession();
  const { header } = appearanceForScope(platform, useAppearanceScope());
  if (!header.showHelpLink || !header.helpUrl) return null;
  return createElement('a', { href: header.helpUrl, target: '_blank', rel: 'noopener noreferrer', style: { fontSize: 'var(--text-sm)', color: 'var(--color-primary-text)' } }, 'Help');
}

/**
 * Wrap the routed page (`<Outlet />`) of the staff shell: on a route owned by a disabled module it
 * renders an explanation instead of the page (the server refuses the module's APIs anyway).
 */
export function ModuleGate({ children }: { children: ReactNode }): ReactNode {
  const { platform } = useSession();
  const { pathname } = useLocation();
  const key = moduleForPath(pathname);
  if (!key || isModuleEnabled(key, platform)) return children;
  return createElement(EmptyState, {
    title: 'This module is disabled by the platform administrator',
    description: `${moduleLabel(key, 'This module', platform)} is turned off for all workspaces. Contact your platform administrator if you need it.`,
  });
}
